import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PhoneIcon from "@mui/icons-material/Phone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Avatar, Box, Chip, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { widgetSmallHeight } from "../../../utils";
import { WidgetComponentCharts } from "../../common/WidgetComponentCharts";
import { WidgetMenuActions } from "../../common/WidgetMenuActions/WidgetMenuActions";
import { SASMenuStyles } from "../../ui/styles/inputStyles";
import { getAllCompanyNumbers } from "./actions/get-all-numbers.actions";
import { ContactCenterDashboard } from "./ContactCenterDashboard";
import { CompanyNumberItem } from "./types";

export const ContactCenterWidget: React.FC = () => {
  const [numbers, setNumbers] = useState<CompanyNumberItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    getAllCompanyNumbers({ order: "DESC", orderBy: "createdAt" })
      .then((data) => setNumbers(data))
      .catch((err) => console.error("Error loading widget numbers:", err))
      .finally(() => setLoading(false));
  }, []);

  const previewItems = numbers.slice(0, 3);
  const activeCount = numbers.filter((n) => n.status.toLowerCase() === "active").length;
  const pendingCount = numbers.filter((n) => n.status.toLowerCase() === "pending").length;

  const getWidgetActions = () => [
    {
      label: "See Contact Center",
      icon: <VisibilityIcon />,
      modalComponent: <ContactCenterDashboard />,
      modalKey: "Dashboard-Contact-Center-Modal",
    },
  ];

  return (
    <WidgetComponentCharts
      swapyName="contact-center"
      sxProp={{ height: widgetSmallHeight }}
      title="Contact Center"
      icon={<HeadsetMicIcon />}
      description={loading ? "Loading lines..." : `${activeCount} active lines • ${pendingCount} pending`}
      actions={<WidgetMenuActions actionList={getWidgetActions()} sx={SASMenuStyles} />}
      data={
        <Box sx={{ width: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* Metrics summary row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
              bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
              p: 1.25,
              borderRadius: "12px",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
            }}
          >
            <Box>
              <Typography fontSize={11} fontWeight={600} color="var(--fx-surface-textsecondary, #64748B)">
                Total Lines
              </Typography>
              <Typography fontSize={16} fontWeight={800} color="var(--fx-surface-text, #0F172A)">
                {numbers.length}
              </Typography>
            </Box>

            <Box>
              <Typography fontSize={11} fontWeight={600} color="#15803D">
                Active
              </Typography>
              <Typography fontSize={16} fontWeight={800} color="#16A34A">
                {activeCount}
              </Typography>
            </Box>

            <Box>
              <Typography fontSize={11} fontWeight={600} color="#B45309">
                Pending
              </Typography>
              <Typography fontSize={16} fontWeight={800} color="#D97706">
                {pendingCount}
              </Typography>
            </Box>
          </Box>

          {/* Numbers preview stack */}
          <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {previewItems.map((item) => (
              <Box
                key={item.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: "var(--fx-colors-info-50, #EFF6FF)",
                      color: "#0284C7",
                    }}
                  >
                    <PhoneIcon style={{ fontSize: 14 }} />
                  </Avatar>
                  <Box minWidth={0}>
                    <Typography variant="body2" fontSize={13} fontWeight={700} color="var(--fx-surface-text, #0F172A)" noWrap>
                      {item.phoneNumber}
                    </Typography>
                    <Typography variant="caption" fontSize={11} color="var(--fx-surface-textsecondary, #64748B)" noWrap display="block">
                      {item.companyName || item.friendlyName}
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                  {item.status.toLowerCase() === "active" ? (
                    <Chip
                      icon={<CheckCircleOutlineIcon style={{ fontSize: 12 }} />}
                      label="Active"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: "var(--fx-colors-success-100, #D1FAE5)",
                        color: "#15803D",
                        borderRadius: "6px",
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<PendingActionsIcon style={{ fontSize: 12 }} />}
                      label="Pending"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: "var(--fx-colors-yellow-100, #FFF9C2)",
                        color: "#B45309",
                        borderRadius: "6px",
                      }}
                    />
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      }
    />
  );
};

export default ContactCenterWidget;
